import { useState, useMemo } from 'react'
import { Box, Text } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom'
import Map, { Marker, Popup } from 'react-map-gl/maplibre'
import { getMapStyle } from '../../lib/mapConfig'
import type { BusinessWorksiteItem } from '../../types/business'
import 'maplibre-gl/dist/maplibre-gl.css'

interface BusinessWorksiteMapProps {
  worksites: BusinessWorksiteItem[]
  initialViewState: { longitude: number; latitude: number; zoom: number }
  /**
   * On mobile, bleed only the map canvas edge-to-edge (matching the base card
   * padding) while keeping the card title + rounded corners in place. Without
   * this the caller must NOT wrap the whole card in a negative margin.
   */
  fullBleedOnMobile?: boolean
}

export function BusinessWorksiteMap({ worksites, initialViewState, fullBleedOnMobile }: BusinessWorksiteMapProps) {
  const navigate = useNavigate()
  const [popupWorksite, setPopupWorksite] = useState<BusinessWorksiteItem | null>(null)

  const geoWorksites = useMemo(() => worksites.filter(w => w.latitude != null && w.longitude != null), [worksites])

  if (geoWorksites.length === 0) return null

  return (
    <Box bg="surface" p={{ base: 4, md: 6 }} borderRadius="lg" shadow="md">
      <Text fontSize="lg" fontWeight="semibold" mb={3}>Locations</Text>
      <Box
        borderRadius={{ base: fullBleedOnMobile ? 'none' : 'md', md: 'md' }}
        overflow="hidden"
        h="350px"
        mx={fullBleedOnMobile ? { base: -4, md: 0 } : 0}
      >
        <Map
          initialViewState={initialViewState}
          style={{ width: '100%', height: '100%' }}
          mapStyle={getMapStyle()}
          attributionControl={false}
        >
          {geoWorksites.map(ws => (
            <Marker
              key={ws.id}
              longitude={ws.longitude!}
              latitude={ws.latitude!}
              color="#DD6B20"
              onClick={e => {
                e.originalEvent.stopPropagation()
                setPopupWorksite(ws)
              }}
              style={{ cursor: 'pointer' }}
            />
          ))}
          {popupWorksite && popupWorksite.latitude != null && popupWorksite.longitude != null && (
            <Popup
              longitude={popupWorksite.longitude}
              latitude={popupWorksite.latitude}
              closeButton={true}
              closeOnClick={false}
              anchor="bottom"
              offset={15}
              onClose={() => setPopupWorksite(null)}
            >
              <Box p={1} maxW="200px">
                <Text
                  fontWeight="bold"
                  fontSize="sm"
                  color="accent"
                  cursor="pointer"
                  _hover={{ textDecoration: 'underline' }}
                  onClick={() => navigate(`/dashboard/worksites/${popupWorksite.id}`)}
                >
                  {popupWorksite.label}
                </Text>
                {popupWorksite.type && (
                  <Text fontSize="xs" color="gray.500">{popupWorksite.type}</Text>
                )}
                <Text fontSize="xs" color="gray.600">
                  {[popupWorksite.city, popupWorksite.state].filter(Boolean).join(', ')}
                </Text>
                <Text fontSize="xs" color="gray.500">
                  {popupWorksite.employeeCount} employee{popupWorksite.employeeCount !== 1 ? 's' : ''}
                </Text>
              </Box>
            </Popup>
          )}
        </Map>
      </Box>
    </Box>
  )
}
